import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';
import { counsellorPresenceService } from '../services/counsellorPresenceService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const profile = await authService?.getUserProfile(userId)
        setUserProfile(profile)
      } catch (error) {
        console.error('Profile load error:', error)
        // If profile load fails due to auth error, clear session
        if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('JWT')) {
          await handleInvalidSession()
        }
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Handle invalid session by clearing storage and state
  const handleInvalidSession = async () => {
    try {
      // Clear Supabase session
      await supabase?.auth?.signOut()
      // Clear local state
      setUser(null)
      profileOperations?.clear()
      // Clear any corrupted storage
      const storageKey = `sb-${new URL(import.meta.env?.VITE_SUPABASE_URL)?.hostname?.split('.')?.[0]}-auth-token`
      localStorage?.removeItem(storageKey)
    } catch (error) {
      console.error('Error clearing invalid session:', error)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      // Handle token refresh errors - if refresh fails, clear session
      if (event === 'TOKEN_REFRESHED') {
        if (!session) {
          console.warn('Token refresh failed, clearing session');
          handleInvalidSession();
          return;
        }
      }

      // Handle signed out event
      if (event === 'SIGNED_OUT') {
        setUser(null);
        profileOperations?.clear();
        setLoading(false);
        return;
      }

      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id); // Fire-and-forget
        // Update presence for institute users (fire-and-forget)
        counsellorPresenceService?.updatePresence(session?.user?.id);
      } else {
        profileOperations?.clear();
      }
    }
  }

  useEffect(() => {
    // Initial session check with error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession()
        
        if (error) {
          console.error('Session retrieval error:', error)
          // If there's an error getting session (like invalid refresh token), clear it
          if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('refresh_token_not_found') ||error?.message?.includes('JWT')) {
            await handleInvalidSession()
          }
          setLoading(false)
          return
        }
        
        authStateHandlers?.onChange(null, session)
      } catch (error) {
        console.error('Unexpected session error:', error)
        await handleInvalidSession()
        setLoading(false)
      }
    }

    initializeAuth()

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Periodically update presence for logged-in users (every 2 minutes)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      counsellorPresenceService?.updatePresence(user?.id);
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const result = await authService?.signInWithEmail(email, password)
      return { data: result, error: null }
    } catch (error) {
      return { data: null, error: error };
    }
  }

  const signOut = async () => {
    try {
      await authService?.signOut()
      setUser(null)
      profileOperations?.clear()
      return { error: null }
    } catch (error) {
      return { error: { message: error?.message || 'Sign out failed' } };
    }
  }

  const signUpStudent = async (userData) => {
    try {
      const result = await authService?.signUpStudent(userData)
      return { data: result, error: null }
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Sign up failed' } };
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('users')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (error) throw error
      setUserProfile(prev => ({ ...prev, ...data }))
      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Update failed' } };
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    signUpStudent,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
